import { CustomContentProps, closeSnackbar } from "notistack";
import { forwardRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquareText, X } from "lucide-react";
import LeadAvatar from "@/components/lead-avatar/lead-avatar";
import type { TSmsToast } from "@/hooks/useInboundSmsNotifier";
import { cn } from "@/lib/utils";

interface NewSmsNotificationProps extends CustomContentProps {
  sms: TSmsToast;
}

/** Toast for a lead's inbound text; clicking it opens the thread (Inbox when the viewer can see it, else the lead). */
const NewSmsNotification = forwardRef<HTMLDivElement, NewSmsNotificationProps>(({ id, sms }, ref) => {
  const navigate = useNavigate();
  const name = sms.name;

  const handleClick = useCallback(() => {
    closeSnackbar(id);
    navigate(sms.targetUrl);
  }, [id, sms.targetUrl, navigate]);

  const handleDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      closeSnackbar(id);
    },
    [id]
  );

  return (
    <div
      ref={ref}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      role="alert"
      tabIndex={0}
      className={cn(
        "relative w-[350px] cursor-pointer rounded-lg border border-[rgba(81,81,81,0.40)] p-4 outline-none backdrop-blur-[15px] transition-all duration-200",
        "bg-[linear-gradient(93deg,rgba(245,158,11,0.22)_0.14%,rgba(245,158,11,0.05)_99.26%)]",
        "shadow-[0_8px_32px_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.05)]",
        "hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-accent-amber/50"
      )}
    >
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={handleDismiss}
        className="absolute right-3 top-3 grid h-6 w-6 place-items-center rounded text-slate-400 transition-colors hover:text-white"
      >
        <X className="h-3 w-3" />
      </button>

      <div className="mb-3 flex items-center gap-2.5">
        <MessageSquareText size={16} className="text-accent-amber" />
        <span className="text-base font-bold leading-6 text-accent-amber">New text message</span>
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-white/[0.10] p-3">
        {/* Not expandable: the toast dismisses itself on click. */}
        <LeadAvatar
          name={name}
          avatarUrl={sms.leadAvatar}
          expandable={false}
          className="h-11 w-11 rounded-xl text-base font-medium"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-medium text-white">{name}</div>
          <div className="truncate text-[13px] text-slate-300">{sms.preview}</div>
        </div>
      </div>
    </div>
  );
});

NewSmsNotification.displayName = "NewSmsNotification";

export default NewSmsNotification;
